import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    MdAccessTime,
    MdHistory,
    MdAdd,
    MdChevronRight,
    MdNotifications,
    MdCheckCircle,
    MdInfo,
    MdStar,
    MdCardGiftcard,
} from "react-icons/md";

// API Importu
import { appointmentsAPI } from "services/api";

// --- SKELETON LOADERS ---
const DashboardSkeleton = () => (
    <div className="flex flex-col gap-6 animate-pulse mt-4">
        <div className="h-[280px] w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
                <div key={i} className="h-[200px] w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
            ))}
        </div>
    </div>
);

const CustomerDashboard = () => {
    // --- STATELER (WORK-LOGIC BİREBİR) ---
    const [nextAppointment, setNextAppointment] = useState(null);
    const [totalAppointments, setTotalAppointments] = useState(0);
    const [recentNotifications, setRecentNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- YARDIMCI FONKSİYONLAR (GÜVENLİ TARİH İŞLEME) ---
    const createSafeDate = (dateStr, timeStr) => {
        try {
            const pureDateStr = dateStr.split("T")[0];
            const dateParts = pureDateStr.split("-");
            const timeParts = timeStr.split(":");
            return new Date(
                Number.parseInt(dateParts[0]),
                Number.parseInt(dateParts[1]) - 1,
                Number.parseInt(dateParts[2]),
                Number.parseInt(timeParts[0]),
                Number.parseInt(timeParts[1]),
                Number.parseInt(timeParts[2] || 0)
            );
        } catch (e) { return null; }
    };

    const createSafeUpdateDate = (dateStr) => {
        try {
            if (!dateStr) return new Date();
            return new Date(dateStr.replace(" ", "T"));
        } catch (e) { return new Date(); }
    };

    // --- VERİ ÇEKME VE İŞLEME ---
    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            try {
                const data = await appointmentsAPI.getMyAppointments();
                if (data.success && data.appointments) {
                    const appointments = data.appointments;
                    const now = new Date();

                    // 1. Toplam Aktif Randevu Sayısı
                    const activeAppointments = appointments.filter((app) => app.status !== "cancelled");
                    setTotalAppointments(activeAppointments.length);

                    // 2. Sıradaki Randevuyu Bul (En Yakın)
                    const upcomingAppointments = appointments
                        .filter((app) => {
                            if (!app.service || !app.staff) return false;
                            const appDateTime = createSafeDate(app.appointment_date, app.appointment_time);
                            return appDateTime && appDateTime > now && (app.status === "pending" || app.status === "approved");
                        })
                        .sort((a, b) => createSafeDate(a.appointment_date, a.appointment_time) - createSafeDate(b.appointment_date, b.appointment_time));

                    if (upcomingAppointments.length > 0) {
                        const nextApp = upcomingAppointments[0];
                        const appDate = createSafeDate(nextApp.appointment_date, nextApp.appointment_time);

                        // TARİH FORMATLAMA: "29 Nisan" ve "Çarşamba" ayrımı
                        const dateOnly = new Intl.DateTimeFormat("tr-TR", { day: "numeric", month: "long" }).format(appDate);
                        const dayName = new Intl.DateTimeFormat("tr-TR", { weekday: "long" }).format(appDate);

                        setNextAppointment({
                            id: nextApp.id,
                            service: nextApp.service?.name || "Hizmet",
                            barberName: `${nextApp.staff?.name} ${nextApp.staff?.surname}`,
                            date: dateOnly,
                            dayName: dayName,
                            time: nextApp.appointment_time.substring(0, 5),
                        });
                    } else { setNextAppointment(null); }

                    // 3. Bildirim Üretimi (CoreUI Mantığı)
                    const generatedNotifications = [];
                    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
                    tomorrow.setHours(0,0,0,0);
                    const dateFormatter = new Intl.DateTimeFormat("tr-TR", { day: "numeric", month: "long" });

                    for (const app of appointments) {
                        if (!app.service || !app.staff) continue;
                        const appDateTime = createSafeDate(app.appointment_date, app.appointment_time);
                        if (!appDateTime) continue;

                        const isTomorrow = new Date(appDateTime).setHours(0,0,0,0) === tomorrow.getTime();
                        const appDateStr = dateFormatter.format(appDateTime);
                        const safeUpdatedAt = createSafeUpdateDate(app.updated_at);

                        if (app.status === "completed" && !app.rating) {
                            generatedNotifications.push({ id: `review-${app.id}`, type: "review", text: `${app.service.name} hizmetinizi değerlendirin.`, sortDate: safeUpdatedAt });
                        }
                        if (app.status === "approved" && isTomorrow) {
                            generatedNotifications.push({ id: `reminder-${app.id}`, type: "info", text: `Yarın ${app.appointment_time.substring(0, 5)} randevunuzu unutmayın.`, sortDate: new Date() });
                        }
                        if (app.status === "approved" && appDateTime > now && !isTomorrow) {
                            generatedNotifications.push({ id: `approved-${app.id}`, type: "success", text: `${appDateStr} ${app.appointment_time.substring(0, 5)} randevunuz onaylandı.`, sortDate: safeUpdatedAt });
                        }
                        if (app.status === "cancelled") {
                            generatedNotifications.push({ id: `cancelled-${app.id}`, type: "danger", text: `${appDateStr} randevunuz iptal edildi.`, sortDate: safeUpdatedAt });
                        }
                    }
                    generatedNotifications.sort((a, b) => b.sortDate - a.sortDate);
                    setRecentNotifications(generatedNotifications.slice(0, 3));
                }
            } catch (err) { console.error(err); } finally { setLoading(false); }
        };
        fetchDashboardData();
    }, []);

    const getNotificationIcon = (type) => {
        switch (type) {
            case "success": return { icon: <MdCheckCircle />, colorClass: "text-green-500", bgClass: "bg-green-50 dark:bg-green-500/10" };
            case "info": return { icon: <MdInfo />, colorClass: "text-brand-500", bgClass: "bg-brand-50 dark:bg-brand-400/10" };
            case "review": return { icon: <MdStar />, colorClass: "text-amber-500", bgClass: "bg-amber-50 dark:bg-amber-500/10" };
            case "danger": return { icon: <MdNotifications />, colorClass: "text-red-500", bgClass: "bg-red-50 dark:bg-red-500/10" };
            default: return { icon: <MdNotifications />, colorClass: "text-gray-500", bgClass: "bg-gray-100 dark:bg-navy-900" };
        }
    };

    if (loading) return <DashboardSkeleton />;

    return (
        <div className="mt-5 flex w-full flex-col gap-6 pb-20 animate-fadeIn">

            {/* 1. HERO BANNER - TEMA RENGİNE DUYARLI VE VURGULU */}
            <div className="w-full">
                {nextAppointment ? (
                    <div className="relative flex flex-col justify-center rounded-[20px] bg-gradient-to-br from-brand-500 to-brand-600 dark:from-brand-400 dark:to-brand-500 p-8 md:p-10 shadow-lg shadow-brand-500/20 overflow-hidden min-h-[280px]">
                        <div className="absolute -top-10 -right-10 opacity-10"><MdAccessTime className="text-[250px] text-white" /></div>
                        <div className="relative z-10 flex flex-col items-start text-white">
                            <div className="flex items-center mb-4">
                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20 backdrop-blur-md border border-white/10 mr-4"><MdAccessTime className="text-2xl" /></div>
                                <h4 className="text-sm font-bold uppercase tracking-widest opacity-90">Sıradaki Randevunuz</h4>
                            </div>
                            <h2 className="text-4xl md:text-5xl font-black mb-8">{nextAppointment.service}</h2>

                            <div className="flex flex-col md:flex-row gap-4 mb-8 w-full">
                                {/* Personel Bilgisi */}
                                <div className="flex items-center gap-3 bg-white/10 px-5 py-3 rounded-2xl border border-white/10 flex-1 md:flex-none">
                                    <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center text-xl"><MdCheckCircle /></div>
                                    <div>
                                        <span className="block text-[10px] font-bold uppercase opacity-70">Personel</span>
                                        <span className="block text-sm font-bold">{nextAppointment.barberName}</span>
                                    </div>
                                </div>

                                {/* VURGULU TARİH VE SAAT (İşte Burası!) */}
                                <div className="flex items-center gap-3 bg-white/10 px-5 py-3 rounded-2xl border border-white/10 flex-1 md:flex-none">
                                    <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center text-xl"><MdAccessTime /></div>
                                    <div>
                                        <span className="block text-[10px] font-bold uppercase opacity-70">Zaman</span>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-sm font-bold whitespace-nowrap">{nextAppointment.date}</span>
                                            {/* Gün İsmi Vurgusu */}
                                            <span className="bg-white text-brand-500 dark:bg-navy-900 dark:text-brand-400 px-2.5 py-0.5 rounded-lg text-[11px] font-black uppercase shadow-sm">
                        {nextAppointment.dayName}
                      </span>
                                            {/* Saat Vurgusu */}
                                            <span className="bg-brand-900/30 text-white px-2.5 py-0.5 rounded-lg text-[11px] font-black shadow-sm">
                        {nextAppointment.time}
                      </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <Link to="/customer/appointments" className="bg-white text-brand-500 dark:bg-navy-900 font-bold px-10 py-3 rounded-xl shadow-md hover:scale-105 transition-all">Randevu Detayları</Link>
                        </div>
                    </div>
                ) : (
                    /* BOŞ DURUM BANNERI */
                    <div className="relative flex flex-col items-center justify-center text-center rounded-[20px] bg-gradient-to-br from-brand-500 to-brand-400 dark:from-navy-800 dark:to-navy-700 p-8 md:p-12 shadow-lg shadow-brand-500/10 overflow-hidden min-h-[280px]">
                        <div className="absolute -bottom-10 -left-10 opacity-5"><MdAdd className="text-[250px] text-white" /></div>
                        <div className="relative z-10 flex flex-col items-center">
                            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/20 dark:bg-brand-400/20 backdrop-blur-md border border-white/20 mb-6 shadow-xl">
                                <MdAdd className="text-5xl text-white dark:text-brand-400" />
                            </div>
                            <h2 className="text-3xl md:text-4xl font-black text-white mb-3">Harika bir güne hazır mısın?</h2>
                            <p className="text-base text-white/80 mb-8 max-w-lg">Yaklaşan bir randevun bulunmuyor. Tarzını yenilemenin tam zamanı!</p>
                            <Link to="/customer/booking" className="bg-white dark:bg-brand-400 text-brand-500 dark:text-navy-900 font-black px-10 py-4 rounded-full shadow-2xl hover:scale-105 active:scale-95 transition-all">Hemen Randevu Al</Link>
                        </div>
                    </div>
                )}
            </div>

            {/* 2. ALT GRID - İSTATİSTİK VE BİLDİRİMLER */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

                {/* TOPLAM RANDEVU */}
                <div className="flex flex-col items-center justify-center rounded-[20px] bg-white dark:bg-navy-800 p-8 shadow-sm border border-gray-100 dark:border-white/5">
                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-50 dark:bg-brand-400/10 text-brand-500 dark:text-brand-400 text-4xl mb-4 shadow-inner"><MdHistory /></div>
                    <h2 className="text-5xl font-black text-navy-700 dark:text-white mb-1">{totalAppointments}</h2>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Toplam Randevu</p>
                </div>

                {/* HIZLI İŞLEMLER */}
                <div className="flex flex-col rounded-[20px] bg-white dark:bg-navy-800 shadow-sm border border-gray-100 dark:border-white/5 overflow-hidden">
                    <div className="flex items-center gap-3 p-5 border-b border-gray-50 dark:border-white/10">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-500 text-white shadow-md"><MdAdd className="text-xl" /></div>
                        <h5 className="text-lg font-bold text-navy-700 dark:text-white">Hızlı İşlemler</h5>
                    </div>
                    <div className="flex flex-col p-2">
                        <Link to="/customer/booking" className="group flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-navy-900/50 transition-all">
                            <div className="flex items-center gap-4">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 dark:bg-brand-400/10 text-brand-500"><MdAdd className="text-xl" /></div>
                                <span className="text-sm font-bold text-navy-700 dark:text-white">Yeni Randevu Al</span>
                            </div>
                            <MdChevronRight className="text-xl text-gray-400 group-hover:text-brand-500" />
                        </Link>
                        <Link to="/customer/appointments" className="group flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-navy-900/50 transition-all">
                            <div className="flex items-center gap-4">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 dark:bg-brand-400/10 text-brand-500"><MdHistory className="text-xl" /></div>
                                <span className="text-sm font-bold text-navy-700 dark:text-white">Randevu Geçmişim</span>
                            </div>
                            <MdChevronRight className="text-xl text-gray-400 group-hover:text-brand-500" />
                        </Link>
                    </div>
                </div>

                {/* SON BİLDİRİMLER */}
                <div className="flex flex-col rounded-[20px] bg-white dark:bg-navy-800 shadow-sm border border-gray-100 dark:border-white/5 overflow-hidden">
                    <div className="flex items-center gap-3 p-5 border-b border-gray-50 dark:border-white/10">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-500 text-white shadow-md"><MdNotifications className="text-xl" /></div>
                        <h5 className="text-lg font-bold text-navy-700 dark:text-white">Son Bildirimler</h5>
                    </div>
                    <div className="flex flex-col p-3 flex-1 overflow-y-auto max-h-[160px]">
                        {recentNotifications.length > 0 ? (
                            recentNotifications.map((notif) => {
                                const { icon, colorClass, bgClass } = getNotificationIcon(notif.type);
                                return (
                                    <div key={notif.id} className="flex items-start gap-4 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-navy-900/30 transition-all">
                                        <div className={`flex h-9 w-9 flex-none items-center justify-center rounded-full ${bgClass} ${colorClass} text-lg shadow-sm`}>{icon}</div>
                                        <p className="text-xs font-bold text-navy-700 dark:text-white leading-snug mt-1.5">{notif.text}</p>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="flex flex-col items-center justify-center py-10 opacity-30">
                                <MdNotifications className="text-5xl mb-2" />
                                <span className="text-xs font-bold">Yeni bildirim yok.</span>
                            </div>
                        )}
                    </div>
                    {recentNotifications.length > 0 && (
                        <div className="p-3 bg-gray-50/50 dark:bg-white/5 text-center border-t border-gray-50 dark:border-white/10">
                            <Link to="/customer/notifications" className="text-xs font-bold text-brand-500 uppercase tracking-widest hover:underline">Tümünü Gör</Link>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default CustomerDashboard;